import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useQueryClient, QueryCache } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/spinner';
import { api, UnauthorizedError } from './api.js';
import { AppShell } from './AppShell.js';
import { Login } from './Login.js';
import { HomePage } from './pages/Home.js';
import { UsersPage } from './pages/Users.js';
import { UserDetailPage } from './pages/UserDetail.js';
import { WorkspacesPage } from './pages/Workspaces.js';
import { ChangesPage } from './pages/Changes.js';
import { ChangeDetailPage } from './pages/ChangeDetail.js';
import { DepotPage } from './pages/Depot.js';
import LorePage from './pages/Lore.js';
import GitPage from './pages/Git.js';
import SearchPage from './pages/Search.js';
import { PerforceGettingStartedPage, LoreGettingStartedPage } from './pages/GettingStarted.js';
import { AdminPage } from './pages/Admin.js';

type AuthState =
  | { kind: 'checking' }
  | { kind: 'anon' }
  | { kind: 'in'; user: string };

export function App() {
  const queryClient = useQueryClient();
  const [auth, setAuth] = useState<AuthState>({ kind: 'checking' });

  useEffect(() => {
    const cache: QueryCache = queryClient.getQueryCache();
    const unsub = cache.subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.query.state.error instanceof UnauthorizedError
      ) {
        setAuth({ kind: 'anon' });
        queryClient.clear();
      }
    });
    return () => unsub();
  }, [queryClient]);

  useEffect(() => {
    api
      .me()
      .then((me) => setAuth({ kind: 'in', user: me.user }))
      .catch((err) => {
        setAuth({ kind: 'anon' });
        if (!(err instanceof UnauthorizedError)) {
          console.warn('me() failed:', err);
        }
      });
  }, []);

  if (auth.kind === 'checking') {
    return (
      <div className="mx-auto flex max-w-md items-center justify-center gap-3 pt-24">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }

  if (auth.kind === 'anon') {
    return (
      <Login
        onSuccess={(user) => {
          setAuth({ kind: 'in', user });
          void queryClient.invalidateQueries();
        }}
      />
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            <AppShell
              user={auth.user}
              onLogout={async () => {
                await api.logout().catch(() => {});
                queryClient.clear();
                setAuth({ kind: 'anon' });
              }}
            />
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:user" element={<UserDetailPage />} />
          <Route path="/workspaces" element={<WorkspacesPage />} />
          <Route path="/changes" element={<ChangesPage />} />
          <Route path="/changes/:n" element={<ChangeDetailPage />} />
          <Route path="/depot/*" element={<DepotPage />} />
          <Route path="/lore/*" element={<LorePage />} />
          <Route path="/git/*" element={<GitPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/getting-started/perforce" element={<PerforceGettingStartedPage />} />
          <Route path="/getting-started/lore" element={<LoreGettingStartedPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
